import { computed, reactive, shallowRef } from 'vue';
import { useStore } from 'vuex';
import { apiRequest, assertOk } from '@/api/http';
import { DEFAULT_AVATAR, useProfilePhoto } from '@/composables/useProfilePhoto';

const FIELDS = ['fname', 'mname', 'lname', 'suffix', 'sex', 'bdate', 'mobile_no'];
const PHOTO_TYPES = ['image/png', 'image/jpeg'];
const PHOTO_MAX_BYTES = 5 * 1024 * 1024;

export const photoUrl = (filename) =>
  `${process.env.VUE_APP_SERVER}/user-details/getProfileImg/${filename || DEFAULT_AVATAR}`;

// The signed-in user's own profile: personal details (saved together) and
// the profile photo (uploaded as soon as it is picked).
export function useProfileSettings() {
  const store = useStore();

  const form = reactive(Object.fromEntries(FIELDS.map((k) => [k, ''])));
  const original = shallowRef({ ...form });
  const photo = shallowRef('');
  const loading = shallowRef(true);
  const saving = shallowRef(false);
  const uploading = shallowRef(false);
  const removing = shallowRef(false);
  // The user's own photo, not the shared default avatar.
  const hasPhoto = shallowRef(false);
  const isOwnPhoto = (filename) => !!filename && filename !== DEFAULT_AVATAR && filename !== 'img_avatar';
  const savedAt = shallowRef(0);

  const isDirty = computed(() =>
    FIELDS.some((k) => (form[k] ?? '') !== (original.value[k] ?? '')),
  );

  async function load() {
    loading.value = true;
    try {
      const data = (await apiRequest('get', '/user-details/getPersonalInfo')) || {};
      for (const k of FIELDS) form[k] = data[k] ?? '';
      original.value = { ...form };
      photo.value = photoUrl(data.profile_img);
      hasPhoto.value = isOwnPhoto(data.profile_img);
    } finally {
      loading.value = false;
    }
  }

  function reset() {
    Object.assign(form, original.value);
  }

  async function save() {
    saving.value = true;
    try {
      assertOk(await apiRequest('post', '/user-details/updateUser', { ...form }));
      original.value = { ...form };
      savedAt.value = Date.now();
      // Keep the sidebar's name in step without a reload.
      const user = store.state.user;
      if (user) store.dispatch('setUser', { ...user, fname: form.fname, lname: form.lname });
    } finally {
      saving.value = false;
    }
  }

  // Throws with a readable message for files we won't upload.
  async function uploadPhoto(file) {
    if (!PHOTO_TYPES.includes(file.type)) throw new Error('Choose a JPG or PNG image.');
    if (file.size > PHOTO_MAX_BYTES) throw new Error('The image must be 5 MB or smaller.');
    uploading.value = true;
    try {
      const fd = new FormData();
      fd.append('file', file);
      assertOk(await apiRequest('post', '/user-details/uploadimage', fd));
      const data = await apiRequest('get', '/user-details/getPersonalInfo');
      // Cache-bust in case the server reuses the file name.
      photo.value = `${photoUrl(data?.profile_img)}?v=${Date.now()}`;
      hasPhoto.value = isOwnPhoto(data?.profile_img);
      // Update the sidebar's user menu too.
      useProfilePhoto().refresh({ bust: true });
    } finally {
      uploading.value = false;
    }
  }

  // Back to the default avatar; the server deletes the photo file.
  async function removePhoto() {
    removing.value = true;
    try {
      assertOk(await apiRequest('delete', '/user-details/profileImage'));
      photo.value = `${photoUrl()}?v=${Date.now()}`;
      hasPhoto.value = false;
      useProfilePhoto().refresh({ bust: true });
    } finally {
      removing.value = false;
    }
  }

  load();

  return {
    form,
    photo,
    hasPhoto,
    loading,
    saving,
    uploading,
    removing,
    savedAt,
    isDirty,
    reset,
    save,
    uploadPhoto,
    removePhoto,
  };
}
